import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useUserContext } from "../../context/UserContext";
import upsertFilterConfs from '../../utils/filterUpsert';

export default function FilterConfigForm({ type: propType, productId: propProductId, isEmbedded }) {
  const params = useParams();
  const type = propType || params.type;
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout } = useUserContext();

  const API_BASE = process.env.REACT_APP_API_URL;

  const initialProductId = propProductId || location.state?.product_id || "";

  const [formData, setFormData] = useState({
    product_id: initialProductId,
    alk_id: "",
    effective_from: "",
  });

  // Update form when propProductId changes (for embedded mode tab switching)
  useEffect(() => {
    if (propProductId && propProductId !== formData.product_id) {
      setFormData(prev => ({ ...prev, product_id: propProductId }));
    }
  }, [propProductId, formData.product_id]);

  const [productList, setProductList] = useState([
    "9346246506",
    "8255025566",
  ]);

  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState("");
  const [componentsList, setComponentsList] = useState([]);
  const [selectedComponentId, setSelectedComponentId] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Helper to try local then prod API paths
  const tryUrlRequest = useCallback(async (path) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const r = await axios.get(`${API_BASE}${path}`, { headers });
    return r;
  }, [token, API_BASE]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helper to convert date/time to IST date-only string (YYYY-MM-DD)
  const toISTDateOnly = (dateInput) => {
    if (!dateInput) return "";
    try {
      const d = new Date(dateInput);
      // IST offset is +5.5 hours in milliseconds
      const istOffsetMs = 5.5 * 60 * 60 * 1000;
      const ist = new Date(d.getTime() + istOffsetMs);
      return ist.toISOString().split('T')[0];
    } catch (e) {
      return "";
    }
  };

  // Utility: categorize field names into logical groups for form layout
  const fieldGroup = (name) => {
    if (["product_id", "alk_id", "ro_mem_id", "sed_id", "carb_id", "effective_from"].includes(name)) return 'basic';
    if (name.startsWith('cleaning') || name.includes('clean')) return 'cleaning';
    if (["createdAt", "updatedAt"].includes(name)) return 'meta';
    return 'settings';
  };

  const defaultValues = useMemo(() => ({
    ro: {
      product_id: "",
      ro_mem_id: "",
      effective_from: "",
      base_life_days: 730,
      flow_drop_limit_frac: 0.5000,
      p2_increase_limit_frac: 0.0800,
      rejection_drop_limit_pp: 10.000,
      tds_ewma_lambda: 0.2,
      alpha_update_period_days: 60,
      forecast_period_days: 3,
      persist_days: 3,
      safety_factor: 0.9,
      deadband_factor: 2.0,
      env_tds_coeff_per_unit: 0.01,
      env_recovery_coeff: 0.01,
      volume_decay_per_liter: 0.0001,
      cleaning_time_decay_per_day: 0.01,
      cleaning_use_volume: true,
      cleaning_beta_vol_per_ml: 0.0001,
      cleaning_gain: 0.005,
      cleaning_cap: 1.03,
      cleaning_events_max: 3,
      flush_lookback_days: 60,
      force_replace_if_remaining_days_lt: 7,
      // actual_flow_drop_limit_frac removed per CSV spec
      createdAt: "2025-08-10 17:39:57.833139+05:30",
      updatedAt: "2025-08-10 17:39:57.833139+05:30",
    },
    alk: {
      product_id: "",
      alk_id: "",
      effective_from: "",
      base_life_days: 365,
      flow_drop_limit_frac: 0.5,
      tds_limit_pp: 80.0,
      tds_ewma_lambda: 0.2,
      alpha_update_period_days: 60,
      forecast_period_days: 3,
      persist_days: 3,
      safety_factor: 0.9,
      deadband_factor: 2.0,
      env_tds_coeff_per_unit: 0.01,
      env_recovery_coeff: 0.01,
      volume_decay_per_liter: 0.0001,
      cleaning_time_decay_per_day: 0.01,
      cleaning_use_volume: true,
      cleaning_beta_vol_per_ml: 0.0001,
      cleaning_gain: 0.005,
      cleaning_cap: 1.03,
      cleaning_events_max: 3,
      flush_lookback_days: 60,
      force_replace_if_remaining_days_lt: 7,
      createdAt: "2025-11-19 15:08:14.129661+05:30",
      updatedAt: "2025-11-19 15:08:14.129661+05:30",
      actual_flow_drop_limit_frac: "",
    },
    sediment: {
      product_id: "",
      sed_id: "",
      effective_from: "",
      base_life_days: 365,
      flow_drop_limit_frac: 0.3000,
      ntu_increase_limit_frac: -0.0062740,
      tds_ewma_lambda: 0.2000,
      alpha_update_period_days: 60,
      forecast_period_days: 3,
      persist_days: 7,
      safety_factor: 0.9000,
      deadband_factor: 2.0000,
      env_tds_coeff_per_unit: 0.0100,
      volume_decay_per_liter: 0.0001,
      cleaning_time_decay_per_day: 0.0100,
      cleaning_use_volume: true,
      cleaning_beta_vol_per_ml: 0.0001,
      cleaning_gain: 0.0050,
      cleaning_cap: 1.0300,
      cleaning_events_max: 3,
      flush_lookback_days: 60,
      force_replace_if_remaining_days_lt: 7,
      createdAt: "2025-08-18 19:49:37.921671+05:30",
      updatedAt: "2025-08-18 19:49:37.921671+05:30",
      actual_flow_drop_limit_frac: 0.1000,
    },
    carbon: {
      product_id: "",
      carb_id: "",
      effective_from: "",
      base_life_days: 365,
      flow_drop_limit_frac: 0.1000,
      chl_increase_limit_frac: 0.0000123,
      chl_ewma_lambda: 0.5678,
      alpha_update_period_days: 7,
      forecast_period_days: 14,
      persist_days: 3,
      safety_factor: 1.2345,
      deadband_factor: 0.3456,
      env_tds_coeff_per_unit: 0.4567,
      volume_decay_per_liter: 0.1111,
      cleaning_time_decay_per_day: 0.2222,
      cleaning_use_volume: true,
      cleaning_beta_vol_per_ml: 0.3333,
      cleaning_gain: 0.4444,
      cleaning_cap: 0.5555,
      cleaning_events_max: 5,
      flush_lookback_days: 2,
      force_replace_if_remaining_days_lt: 3,
      actual_flow_drop_limit_frac: 0.6789,
      createdAt: "2025-09-08 14:24:57.579753+05:30",
      updatedAt: "2025-09-08 14:24:57.579753+05:30",
    },
  }), []);

  // Field definitions for types. Use exact column names as labels per request.
  const fieldDefs = useMemo(() => ({
    alk: [
      { name: "product_id", type: "select" },
      { name: "alk_id", type: "text" },
      { name: "effective_from", type: "date" },
      { name: "base_life_days", type: "number" },
      { name: "flow_drop_limit_frac", type: "number" },
      { name: "tds_limit_pp", type: "number" },
      { name: "tds_ewma_lambda", type: "number" },
      { name: "alpha_update_period_days", type: "number" },
      { name: "forecast_period_days", type: "number" },
      { name: "persist_days", type: "number" },
      { name: "safety_factor", type: "number" },
      { name: "deadband_factor", type: "number" },
      { name: "env_tds_coeff_per_unit", type: "number" },
      { name: "env_recovery_coeff", type: "number" },
      { name: "volume_decay_per_liter", type: "number" },
      { name: "cleaning_time_decay_per_day", type: "number" },
      { name: "cleaning_use_volume", type: "boolean" },
      { name: "cleaning_beta_vol_per_ml", type: "number" },
      { name: "cleaning_gain", type: "number" },
      { name: "cleaning_cap", type: "number" },
      { name: "cleaning_events_max", type: "number" },
      { name: "flush_lookback_days", type: "number" },
      { name: "force_replace_if_remaining_days_lt", type: "number" },
      { name: "createdAt", type: "date" },
      { name: "updatedAt", type: "date" },
      { name: "actual_flow_drop_limit_frac", type: "number" },
    ],
    ro: [
      { name: "product_id", type: "select" },
      { name: "ro_mem_id", type: "text" },
      { name: "effective_from", type: "date" },
      { name: "base_life_days", type: "number" },
      { name: "flow_drop_limit_frac", type: "number" },
      { name: "p2_increase_limit_frac", type: "number" },
      { name: "rejection_drop_limit_pp", type: "number" },
      { name: "tds_ewma_lambda", type: "number" },
      { name: "alpha_update_period_days", type: "number" },
      { name: "forecast_period_days", type: "number" },
      { name: "persist_days", type: "number" },
      { name: "safety_factor", type: "number" },
      { name: "deadband_factor", type: "number" },
      { name: "createdAt", type: "date" },
      { name: "updatedAt", type: "date" },
      { name: "env_tds_coeff_per_unit", type: "number" },
      { name: "env_recovery_coeff", type: "number" },
      { name: "volume_decay_per_liter", type: "number" },
      { name: "cleaning_time_decay_per_day", type: "number" },
      { name: "cleaning_use_volume", type: "boolean" },
      { name: "cleaning_beta_vol_per_ml", type: "number" },
      { name: "cleaning_gain", type: "number" },
      { name: "cleaning_cap", type: "number" },
      { name: "cleaning_events_max", type: "number" },
      { name: "flush_lookback_days", type: "number" },
      { name: "force_replace_if_remaining_days_lt", type: "number" },
    ],
    sediment: [
      { name: "product_id", type: "select" },
      { name: "sed_id", type: "text" },
      { name: "effective_from", type: "date" },
      { name: "base_life_days", type: "number" },
      { name: "flow_drop_limit_frac", type: "number" },
      { name: "ntu_increase_limit_frac", type: "number" },
      { name: "tds_ewma_lambda", type: "number" },
      { name: "alpha_update_period_days", type: "number" },
      { name: "forecast_period_days", type: "number" },
      { name: "persist_days", type: "number" },
      { name: "safety_factor", type: "number" },
      { name: "deadband_factor", type: "number" },
      { name: "env_tds_coeff_per_unit", type: "number" },
      { name: "volume_decay_per_liter", type: "number" },
      { name: "cleaning_time_decay_per_day", type: "number" },
      { name: "cleaning_use_volume", type: "boolean" },
      { name: "cleaning_beta_vol_per_ml", type: "number" },
      { name: "cleaning_gain", type: "number" },
      { name: "cleaning_cap", type: "number" },
      { name: "cleaning_events_max", type: "number" },
      { name: "flush_lookback_days", type: "number" },
      { name: "force_replace_if_remaining_days_lt", type: "number" },
      { name: "createdAt", type: "date" },
      { name: "updatedAt", type: "date" },
      { name: "actual_flow_drop_limit_frac", type: "number" },
    ],
    carbon: [
      { name: "product_id", type: "select" },
      { name: "carb_id", type: "text" },
      { name: "effective_from", type: "date" },
      { name: "base_life_days", type: "number" },
      { name: "flow_drop_limit_frac", type: "number" },
      { name: "chl_increase_limit_frac", type: "number" },
      { name: "chl_ewma_lambda", type: "number" },
      { name: "alpha_update_period_days", type: "number" },
      { name: "forecast_period_days", type: "number" },
      { name: "persist_days", type: "number" },
      { name: "safety_factor", type: "number" },
      { name: "deadband_factor", type: "number" },
      { name: "env_tds_coeff_per_unit", type: "number" },
      { name: "volume_decay_per_liter", type: "number" },
      { name: "cleaning_time_decay_per_day", type: "number" },
      { name: "cleaning_use_volume", type: "boolean" },
      { name: "cleaning_beta_vol_per_ml", type: "number" },
      { name: "cleaning_gain", type: "number" },
      { name: "cleaning_cap", type: "number" },
      { name: "cleaning_events_max", type: "number" },
      { name: "flush_lookback_days", type: "number" },
      { name: "force_replace_if_remaining_days_lt", type: "number" },
      { name: "actual_flow_drop_limit_frac", type: "number" },
      { name: "createdAt", type: "date" },
      { name: "updatedAt", type: "date" },
    ],
    unified: [
      { name: "product_id", type: "text" },
      { name: "device_model", type: "text" },
      { name: "activation_date", type: "date" },
      { name: "ro_id", type: "text" },
      { name: "alk_id", type: "text" },
      { name: "carbon_id", type: "text" },
      { name: "sediment_id", type: "text" },
      { name: "max_flow_rate", type: "number" },
      { name: "pressure_threshold", type: "number" },
      { name: "service_interval_months", type: "number" },
    ],
  }), []);

  useEffect(() => {
    // Fetch product list from /filterconf/products endpoint
    async function fetchProducts() {
      if (!token) return;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const res = await axios.get(`${API_BASE}/filterconf/products`, { headers });
        const data = res.data?.products ?? res.data?.data ?? res.data;
        if (Array.isArray(data)) {
          setProductList(data.map((p) => p.product_id || p.id || p));
        }
      } catch (err) {
        console.warn("Failed to fetch products; using fallback list.", err.message);
        if (err.response?.status === 401) {
          logout();
          toast.error('Session expired. Please login again.');
        }
      }
    }
    fetchProducts();
  }, [token, API_BASE, navigate, logout]);

  // Ensure default product id is present in the productList so select shows the default value
  useEffect(() => {
    if (type === 'sediment' && defaultValues.sediment?.product_id) {
      const p = defaultValues.sediment.product_id;
      setProductList((prev) => (prev.includes(p) ? prev : [p, ...prev]));
    }
    if (type === 'carbon' && defaultValues.carbon?.product_id) {
      const p = defaultValues.carbon.product_id;
      setProductList((prev) => (prev.includes(p) ? prev : [p, ...prev]));
    }
    if (type === 'ro' && defaultValues.ro?.product_id) {
      const p = defaultValues.ro.product_id;
      setProductList((prev) => (prev.includes(p) ? prev : [p, ...prev]));
    }
  }, [type, defaultValues.sediment, defaultValues.carbon, defaultValues.ro]);

  useEffect(() => {
    // Initialize form data keys when type changes
    const defs = fieldDefs[type] ?? [];
    const newForm = {};
    defs.forEach((d) => {
      // default values
      if (d.type === "boolean") newForm[d.name] = false;
      else newForm[d.name] = "";
      // override defaults using defaultValues map when present
      if (defaultValues[type] && defaultValues[type][d.name] !== undefined) {
        newForm[d.name] = defaultValues[type][d.name];
      }
    });
    // Preserve product_id from props or previous state
    const pid = propProductId || formData.product_id || "";
    setFormData((prev) => ({ ...newForm, product_id: pid }));
  }, [type, fieldDefs, defaultValues, propProductId, formData.product_id]);

  // typeLabel not required; labels are being rendered directly as column names

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Auto-fill component details when product_id is selected
    if (name === 'product_id' && value && type) {
      await fetchAndFillComponentDetails(value);
    }
    if (name === 'component_select' && value) {
      setSelectedComponentId(value);
      await fetchAndFillDetailsForComponent(value);
    }
  };

  const fetchAndFillDetailsForComponent = async (componentId) => {
    if (!componentId || !token) return;
    // Try to find the component in the already fetched componentsList
    const found = componentsList.find((c) => (c.component_id || c.id || c["componentId"]) === componentId || c.id === componentId);
    if (found) {
      mapComponentToForm(found);
      return;
    }

    // If not present, try to fetch by product + type and match
    try {
      let res;
      // Try components/:product_id/:component_type where possible for precise lookup
      const aliasMap = {
        alk: ["alkaline_filter", "alk", "alkaline"],
        carbon: ["carbon_filter", "carb", "carbon"],
        sediment: ["sediment_filter", "sed"],
        ro: ["ro_membrane", "ro", "ro_mem"],
      };
      const typeAliases = aliasMap[type] || [type];
      for (const a of typeAliases) {
        try {
          res = await tryUrlRequest(`/components/${formData.product_id}/${a}`);
        } catch (e) {
          res = null;
        }
        if (res && (res.data?.components || res.data?.data || Array.isArray(res.data))) break;
      }
      // as a fallback, list components for product then find by ID
      if (!res) {
        res = await tryUrlRequest(`/components/${formData.product_id}`);
      }
      const data = res?.data?.components || res?.data?.data || res?.data || null;
      const comps = Array.isArray(data) ? data : [];
      const comp = comps.find((c) => (c.component_id || c.id || c["componentId"]) === componentId || c.id === componentId);
      if (comp) mapComponentToForm(comp);
    } catch (err) {
      console.warn('Failed to fetch component details by id:', err?.message || err);
      if (err.response?.status === 401) {
        logout();
        toast.error('Session expired. Please login again.');
      }
    }
  };

  const mapComponentToForm = useCallback((comp) => {
    if (!comp) return;
    // Map common id names
    const compId = comp.component_id || comp.id || comp.componentId || comp["componentId"];
    if (type === 'ro' && compId) setFormData((prev) => ({ ...prev, ro_mem_id: compId }));
    if (type === 'sediment' && compId) setFormData((prev) => ({ ...prev, sed_id: compId }));
    if (type === 'carbon' && compId) setFormData((prev) => ({ ...prev, carb_id: compId }));
    if (type === 'alk' && compId) setFormData((prev) => ({ ...prev, alk_id: compId }));
    // Map installed_at to effective_from if present
    if (comp.installed_at) {
      try {
        const iso = toISTDateOnly(comp.installed_at);
        setFormData((prev) => ({ ...prev, effective_from: iso }));
      } catch (e) {
        // ignore
      }
    }
    // Copy any matching fields, with special handling for date fields
    Object.keys(comp).forEach((k) => {
      if ((fieldDefs[type] || []).some((f) => f.name === k)) {
        if (k === 'createdAt' || k === 'updatedAt') {
          try {
            const iso = toISTDateOnly(comp[k]);
            setFormData((prev) => ({ ...prev, [k]: iso }));
            return; // continue to next key
          } catch (e) {
            // fall back to raw
          }
        }
        setFormData((prev) => ({ ...prev, [k]: comp[k] }));
      }
    });
  }, [type, fieldDefs]);

  const fetchAndFillComponentDetails = useCallback(async (productId) => {
    if (!productId || !token) return;
    setLoading(true);
    try {
      let res;
      // Fetch components list from /filterconf/components/:productId (preferred), fallback to /components/:productId
      res = await tryUrlRequest(`/filterconf/components/${productId}`) || await tryUrlRequest(`/components/${productId}`);
      // We already attempted `/filterconf/components/:productId` and `/components/:productId` above
      // so at this point `res` will have the components list or be null
      let data = res?.data?.components ?? res?.data?.data ?? res?.data ?? null;
      // If still null, fallback to GET /filterconf/:productId (old behavior)
      if (!data) {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API_BASE}/filterconf/${productId}`, { headers });
        data = res.data?.data ?? res.data;
      }
      // console.log("Fetched component details for auto-fill:", data);

      // The API returns a `configs` object with arrays: { ro: [], sed: [], carb: [], alk: [] }
      // Prefer values from `configs[type]` array if present, otherwise check top-level fields
      const configsObj = data.configs || data;
      const cfgArray = configsObj?.[type] || configsObj?.[type === 'sediment' ? 'sed' : type] || [];
      let cfg = null;
      if (Array.isArray(cfgArray) && cfgArray.length > 0) {
        cfg = cfgArray[0];
      }

      // If we have a `components` list, set components state to allow component selection
      // Support different keys from the API: `components`, or the return itself being an array
      const comps = data.components || data?.components || (Array.isArray(data) ? data : null);
      if (Array.isArray(comps) && comps.length > 0) {
        // Filter components to only relevant ones for the current type
        const serverTypeMap = {
          alk: 'alkaline_filter',
          carbon: 'carbon_filter',
          sediment: 'sediment_filter',
          ro: 'ro_membrane',
        };
        const desiredType = serverTypeMap[type] || type;
        const filtered = comps.filter((c) => ((c.component_type || '').toLowerCase() === desiredType.toLowerCase()));
        const componentsToShow = filtered.length > 0 ? filtered : comps;
        setComponentsList(componentsToShow);
        // If there's only one matched component, auto-select and map it
        if (filtered.length === 1) {
          const single = filtered[0];
          const compId = single.component_id || single.id || single.componentId || single['componentId'];
          setSelectedComponentId(compId);
          mapComponentToForm(single);
        }
      } else if (Array.isArray(cfgArray) && cfgArray.length > 0) {
        // If config array exists but we didn't find separate components, make the components list from cfgArray
        setComponentsList(cfgArray);
      } else {
        setComponentsList([]);
      }

      // helper to map common id keys and name variants
      const findId = (obj, keys) => {
        for (const k of keys) {
          if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
        }
        return undefined;
      };

      if (cfg) {
        console.log('Using first config object to auto-fill:', cfg);
        // Map component ID fields per type
        if (type === 'ro') {
          const val = findId(cfg, ['ro_mem_id', 'ro_id', 'ro', 'id', 'component_id']);
          if (val) setFormData((prev) => ({ ...prev, ro_mem_id: val }));
        }
        if (type === 'sediment') {
          const val = findId(cfg, ['sed_id', 'sediment_id', 'sed', 'id', 'component_id']);
          if (val) setFormData((prev) => ({ ...prev, sed_id: val }));
        }
        if (type === 'carbon') {
          const val = findId(cfg, ['carb_id', 'carbon_id', 'carbon', 'id', 'component_id']);
          if (val) setFormData((prev) => ({ ...prev, carb_id: val }));
        }
        if (type === 'alk') {
          const val = findId(cfg, ['alk_id', 'alkaline_id', 'alk', 'id', 'component_id']);
          if (val) setFormData((prev) => ({ ...prev, alk_id: val }));
        }

        // Auto-fill any other matching keys directly into the form if the form has those keys
        // If cfg contains `installed_at`, map to `effective_from` (date-only)
        if (cfg.installed_at) {
          try {
            const iso = toISTDateOnly(cfg.installed_at);
            setFormData((prev) => ({ ...prev, effective_from: iso }));
          } catch (e) {
            // ignore
          }
        }
        Object.keys(cfg).forEach((key) => {
          // normalize key names but use the raw field name as the form expects
          const normalizedKey = key;
          // if the field exists in the fieldDefs for the type, set it
          if ((fieldDefs[type] || []).some((f) => f.name === normalizedKey)) {
            // convert createdAt/updatedAt to date-only strings for date inputs
            if (normalizedKey === 'createdAt' || normalizedKey === 'updatedAt') {
              try {
                const iso = toISTDateOnly(cfg[normalizedKey]);
                setFormData((prev) => ({ ...prev, [normalizedKey]: iso }));
                return;
              } catch (e) {
                // fall back to raw string
              }
            }
            setFormData((prev) => ({ ...prev, [normalizedKey]: cfg[key] }));
          }
        });
        // If componentsList is empty but cfg exists, add cfg to componentsList to allow selection
        setComponentsList(prev => prev.length === 0 ? [cfg] : prev);
      } else if (data) {
        // fallback: if the config arrays are empty, check top level fields
        // Auto-fill based on type with robust property checking
        if (type === 'ro') {
          const val = findId(data, ['ro_mem_id', 'ro_id', 'ro', 'id']);
          if (val) setFormData((prev) => ({ ...prev, ro_mem_id: val }));
        } else if (type === 'sediment') {
          const val = findId(data, ['sed_id', 'sediment_id', 'sed', 'id']);
          if (val) setFormData((prev) => ({ ...prev, sed_id: val }));
        } else if (type === 'carbon') {
          const val = findId(data, ['carb_id', 'carbon_id', 'carbon', 'id']);
          if (val) setFormData((prev) => ({ ...prev, carb_id: val }));
        } else if (type === 'alk') {
          const val = findId(data, ['alk_id', 'alkaline_id', 'alk', 'id']);
          if (val) setFormData((prev) => ({ ...prev, alk_id: val }));
        }
      }

      // removed toast to avoid popup when component details are auto-loaded
    } catch (err) {
      console.warn('Failed to fetch component details:', err.message);
      toast.error('Could not load component details');
      if (err.response?.status === 401) {
        logout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE, type, mapComponentToForm, fieldDefs, tryUrlRequest, navigate, logout]);

  // Fetch component details when product_id is set (from props or state)
  useEffect(() => {
    const pid = propProductId || formData.product_id;
    if (pid && type && token) {
      fetchAndFillComponentDetails(pid);
    }
  }, [type, propProductId, token, formData.product_id, fetchAndFillComponentDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic front-end validation
    if (!formData.product_id || formData.product_id.trim() === '') {
      return toast.error('Product id is required');
    }
    setSaving(true);
    try {
      // Build payload: include only the api-relevant fields and rename id fields where necessary
      const apiTypeKey = type === 'sediment' ? 'sed' : (type === 'carbon' ? 'carb' : type === 'alk' ? 'alk' : type === 'ro' ? 'ro' : type);

      const payload = { product_id: formData.product_id };
      // Build object for the component
      const compObj = {};
      (fieldDefs[type] || []).forEach((f) => {
        const val = formData[f.name];
        if (val === undefined || val === '') return;
        // rename field names if necessary for API expectations
        let outName = f.name;
        // remap our ui names to API names (no 'ro_id' in RO fields — we use `ro_mem_id` directly)
        // No mapping required as UI field is `carb_id` and API expects `carb_id`
        // sediment and alk ids are same name in UI and API
        // coerce numerics for number fields
        if (f.type === 'number') {
          const num = Number(val);
          compObj[outName] = Number.isNaN(num) ? val : num;
        } else {
          compObj[outName] = val;
        }
      });
      if (Object.keys(compObj).length === 0) {
        setSaving(false);
        return toast.error('No configuration values to save');
      }

      if (type === 'ro') {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API_BASE}/filterconf/upsert`, { product_id: formData.product_id, ro: [compObj] }, { headers });
        toast.success('Configuration updated successfully');
      } else {
        // Send as array to make upsert flexible (server accepts object or array)
        payload[apiTypeKey] = [compObj];

        const result = await upsertFilterConfs(payload, token, { apiBase: API_BASE, upsertPath: '/api/v1/filterconf/upsert' });
        if (result.ok) {
          toast.success('Configuration upserted successfully');
          console.log('Upsert result:', result.data);
          // Optionally navigate back or reset form
        } else {
          if (result.status === 401) {
            logout();
            toast.error('Session expired. Please login again.');
            return;
          }
          toast.error('Failed to upsert: ' + (result.message || 'Unknown error'));
          console.warn('Upsert failed', result);
        }
      }
    } catch (err) {
      console.error('Error while saving config', err);
      toast.error('Unexpected error: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  function addNewProduct() {
    const v = newProduct?.trim();
    if (!v) return toast.error("Enter a valid product id");
    if (productList.includes(v)) return toast.error("Product already exists");
    setProductList((p) => [...p, v]);
    setFormData({ ...formData, product_id: v });
    setNewProduct("");
    setShowAddProduct(false);
    toast.success("Product added");
  }

  return (
    <div className={isEmbedded ? "w-full min-w-0" : "w-full min-w-0 px-3 py-6 sm:px-6 md:px-8 max-w-3xl mx-auto flex justify-center"}>
      <div className={isEmbedded ? "w-full min-w-0" : "w-full min-w-0 bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-md border border-gray-100"}>
        {!isEmbedded && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 capitalize">Add {type} Configuration</h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {loading && (
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-indigo-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" d="M4 12a8 8 0 018-8v8z" fill="currentColor" />
                  </svg>
                  <span>Loading components...</span>
                </div>
              )}
              {formData.product_id && (
                <div className="text-xs sm:text-sm text-gray-600 bg-gray-50 px-2.5 py-1 rounded border">Product: <span className="font-semibold text-gray-800">{formData.product_id}</span></div>
              )}
              {selectedComponentId && (
                <div className="text-xs sm:text-sm text-gray-600 bg-gray-50 px-2.5 py-1 rounded border">Component: <span className="font-semibold text-gray-800">{selectedComponentId}</span></div>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          { /* Render fields dynamically based on type config */}
          {(fieldDefs[type] || []).filter(field => !(isEmbedded && field.name === 'product_id')).map((field) => (
            <div key={field.name}>
              {/* Insert group headings */}
              {fieldGroup(field.name) === 'basic' && (field.name === 'product_id') && (
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">General</h3>
                </div>
              )}
              {fieldGroup(field.name) === 'settings' && (field.name === 'base_life_days') && (
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Filter Settings</h3>
                </div>
              )}
              {fieldGroup(field.name) === 'cleaning' && (field.name === 'cleaning_time_decay_per_day') && (
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Cleaning Settings</h3>
                </div>
              )}
              {fieldGroup(field.name) === 'meta' && (field.name === 'createdAt') && (
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Metadata</h3>
                </div>
              )}
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                {field.name}
              </label>
              {field.type === "select" ? (
                <div className="flex gap-2">
                  {/* Product ID input with suggestions */}
                  <div className="relative w-full">
                    <input
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] ?? ""}
                      onChange={handleChange}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      placeholder="Enter or choose product id"
                      className="w-full p-2 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {showSuggestions && productList.length > 0 && (
                      <ul className="absolute z-40 bg-white border rounded w-full max-h-48 overflow-auto mt-1 shadow">
                        {(formData.product_id || "").toString().trim() === "" ? productList : productList.filter((p) => (p || "").toString().toLowerCase().includes((formData.product_id || "").toString().toLowerCase()))
                          .slice(0, 50)
                          .map((p) => (
                            <li
                              key={p}
                              onMouseDown={(e) => {
                                // onMouseDown to prevent blur before click
                                e.preventDefault();
                                setFormData((prev) => ({ ...prev, product_id: p }));
                                setShowSuggestions(false);
                                // fetch components for selected product
                                fetchAndFillComponentDetails(p);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            >
                              {p}
                            </li>
                          ))}
                        {productList.filter((p) => (p || "").toString().toLowerCase().includes((formData.product_id || "").toString().toLowerCase())).length === 0 && (
                          <li className="px-3 py-2 text-gray-500">No suggestions</li>
                        )}
                      </ul>
                    )}
                  </div>
                  {/* Component selector (shows after product input) */}
                  {componentsList.length > 0 && field.name === 'product_id' && (
                    <select
                      id="component_select"
                      name="component_select"
                      value={selectedComponentId}
                      onChange={handleChange}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">-- Select Component --</option>
                      {componentsList.map((c) => {
                        const cid = c.component_id || c.id || c.componentId || c["componentId"] || c['carb_id'] || c['sed_id'] || c['alk_id'] || c['ro_id'];
                        return (
                          <option key={cid} value={cid}>
                            {cid}
                          </option>
                        );
                      })}
                    </select>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-2 bg-gray-100 border rounded text-sm hover:bg-gray-200"
                      onClick={() => setShowAddProduct((s) => !s)}
                    >
                      {showAddProduct ? "Cancel" : "Add New Product"}
                    </button>
                  </div>
                </div>
              ) : field.type === "boolean" ? (
                <div className="flex items-center gap-2">
                  <input
                    id={field.name}
                    name={field.name}
                    checked={!!formData[field.name]}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
                    type="checkbox"
                  />
                </div>
              ) : (
                <input
                  type={field.type}
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] ?? ""}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  required
                  className="w-full p-2 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`Enter ${field.name}`}
                />
              )}
              {showAddProduct && field.name === "product_id" && (
                <div className="pt-2 flex gap-2">
                  <input
                    type="text"
                    value={newProduct}
                    onChange={(e) => setNewProduct(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="New product id"
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                  <button
                    type="button"
                    onClick={addNewProduct}
                    className="px-4 py-2 text-white bg-indigo-600 rounded hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Additional fields are already rendered dynamically above based on the selected type */}

          <div className="flex gap-4 pt-4">
            {!isEmbedded && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 text-white bg-indigo-600 rounded hover:bg-indigo-700 ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {saving ? (
                <span className="flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /></svg> Saving...</span>
              ) : (
                'Save Configuration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
